import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { flexiBenefitsService } from "../../services/api";
import { uploadFileToR2 } from "../../services/proofApi";

/**
 * ESS — Flexi hub / declare / claim.
 * mode: hub (default) | declare | claim
 */
export default function EmployeeFlexiReimbursementPanel({
  employeeId,
  mode = "hub",
  hideHeader = false,
  onActionSuccess,
}) {
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [components, setComponents] = useState([]);
  const [balances, setBalances] = useState([]);
  const [claims, setClaims] = useState([]);
  const [declarations, setDeclarations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [declForm, setDeclForm] = useState({
    componentCode: "",
    declaredAmount: "",
    declarationJson: {},
  });
  const [claimForm, setClaimForm] = useState({
    componentCode: "",
    salaryComponentId: "",
    claimAmount: "",
    claimPeriodFromMonth: "",
    claimPeriodToMonth: "",
    details: "",
  });
  const [claimDocuments, setClaimDocuments] = useState([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [ledger, setLedger] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accessRes, compRes, balRes, claimRes, declRes, ledgerRes] = await Promise.all([
        flexiBenefitsService.getAccess({ employeeId }),
        flexiBenefitsService.getEligibleComponents({ employeeId }),
        employeeId
          ? flexiBenefitsService.getBalances({ employeeId })
          : Promise.resolve({ data: { balances: [] } }),
        flexiBenefitsService.listClaims({}),
        flexiBenefitsService.listDeclarations({ employeeId }),
        employeeId
          ? flexiBenefitsService.listLedger({ employeeId }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      setAccess(accessRes?.data || null);
      setComponents(compRes?.data || []);
      setBalances(balRes?.data?.balances || []);
      setClaims(claimRes?.data || []);
      setDeclarations(declRes?.data || []);
      setLedger(Array.isArray(ledgerRes?.data) ? ledgerRes.data : ledgerRes?.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load Flexi benefits");
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedComp = useMemo(
    () =>
      components.find(
        (c) => String(c.code).toUpperCase() === String(declForm.componentCode || claimForm.componentCode).toUpperCase()
      ),
    [components, declForm.componentCode, claimForm.componentCode]
  );

  const schema = selectedComp?.flexiConfig?.declarationSchema || selectedComp?.flexiConfigJson?.declarationSchema || [];

  const declareLimits = useMemo(() => {
    if (!selectedComp) return { monthly: null, annual: null };
    const monthly =
      selectedComp.structureMonthlyMax != null
        ? Number(selectedComp.structureMonthlyMax)
        : Number(selectedComp.flexiConfig?.monthlyMax || selectedComp.flexiConfigJson?.monthlyMax) || null;
    let annual =
      selectedComp.structureAnnualMax != null
        ? Number(selectedComp.structureAnnualMax)
        : Number(selectedComp.flexiConfig?.annualMax || selectedComp.flexiConfigJson?.annualMax) || null;
    if ((annual == null || !(annual > 0)) && monthly != null && monthly >= 0) {
      annual = Math.round(monthly * 12 * 100) / 100;
    }
    if ((monthly == null || !(monthly > 0)) && annual != null && annual > 0) {
      return { monthly: Math.round((annual / 12) * 100) / 100, annual };
    }
    return {
      monthly: monthly != null && Number.isFinite(monthly) ? monthly : null,
      annual: annual != null && Number.isFinite(annual) ? annual : null,
    };
  }, [selectedComp]);

  const formatInr = (n) =>
    n == null || !Number.isFinite(Number(n))
      ? "—"
      : new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          maximumFractionDigits: 0,
        }).format(Number(n));

  const monthToPeriodBounds = (ym) => {
    if (!ym || !/^\d{4}-\d{2}$/.test(String(ym))) return { from: null, to: null };
    const [y, m] = String(ym).split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    return {
      from: `${ym}-01`,
      to: `${ym}-${String(last).padStart(2, "0")}`,
    };
  };

  const claimBalance = useMemo(() => {
    const code = String(claimForm.componentCode || "").toUpperCase();
    if (!code) return null;
    const b = balances.find((x) => String(x.componentCode || "").toUpperCase() === code);
    if (!b) {
      return { available: 0, declared: 0, approved: 0 };
    }
    return {
      available: Number(b.availableDeclared) || 0,
      declared: Number(b.declared) || 0,
      approved: Number(b.approved) || 0,
    };
  }, [balances, claimForm.componentCode]);

  const submitDeclaration = async (e) => {
    e.preventDefault();
    if (!declForm.componentCode || !declForm.declaredAmount) {
      toast.error("Select component and declared amount");
      return;
    }
    const declared = Number(declForm.declaredAmount);
    const annualMax = declareLimits.annual;
    if (annualMax != null && annualMax > 0 && declared > annualMax) {
      toast.error(
        `Amount cannot exceed your FY limit (${formatInr(annualMax)}). Max per month is ${formatInr(declareLimits.monthly)}.`
      );
      return;
    }
    setBusy(true);
    try {
      const comp = components.find(
        (c) => String(c.code).toUpperCase() === String(declForm.componentCode).toUpperCase()
      );
      await flexiBenefitsService.upsertDeclaration({
        employeeId,
        componentCode: declForm.componentCode,
        salaryComponentId: comp?.id,
        tdsReferenceHeadId: comp?.tdsReferenceHeadId,
        structureAnnualMax:
          annualMax ||
          comp?.flexiConfig?.annualMax ||
          comp?.flexiConfigJson?.annualMax ||
          declared,
        declaredAmount: declared,
        status: "SUBMITTED",
        declarationJson: declForm.declarationJson,
        flexiConfig: comp?.flexiConfig || comp?.flexiConfigJson,
      });
      toast.success("Opt-in submitted");
      setDeclForm({ componentCode: "", declaredAmount: "", declarationJson: {} });
      await load();
      onActionSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Declaration failed");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (componentCode) => {
    if (!window.confirm(`Revoke declaration for ${componentCode}?`)) return;
    setBusy(true);
    try {
      await flexiBenefitsService.revokeDeclaration({ employeeId, componentCode });
      toast.success("Declaration revoked");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Revoke failed");
    } finally {
      setBusy(false);
    }
  };

  const submitClaim = async (e) => {
    e.preventDefault();
    if (!claimForm.componentCode || !claimForm.claimAmount) {
      toast.error("Select component and claim amount");
      return;
    }
    if (!claimForm.claimPeriodFromMonth || !claimForm.claimPeriodToMonth) {
      toast.error("Select claim period months");
      return;
    }
    if (claimForm.claimPeriodToMonth < claimForm.claimPeriodFromMonth) {
      toast.error("Period to month cannot be before period from");
      return;
    }
    const amount = Number(claimForm.claimAmount);
    const available = claimBalance?.available;
    if (available != null && available >= 0 && amount > available) {
      toast.error(`Claim cannot exceed available balance (${formatInr(available)})`);
      return;
    }
    if (!(amount > 0)) {
      toast.error("Claim amount must be greater than zero");
      return;
    }
    setBusy(true);
    try {
      const comp = components.find(
        (c) => String(c.code).toUpperCase() === String(claimForm.componentCode).toUpperCase()
      );
      const fromBounds = monthToPeriodBounds(claimForm.claimPeriodFromMonth);
      const toBounds = monthToPeriodBounds(claimForm.claimPeriodToMonth);
      await flexiBenefitsService.submitClaim({
        employeeId,
        componentCode: claimForm.componentCode,
        salaryComponentId: claimForm.salaryComponentId || comp?.id,
        tdsReferenceHeadId: comp?.tdsReferenceHeadId,
        structureAnnualMax:
          declareLimits.annual ||
          comp?.structureAnnualMax ||
          comp?.flexiConfig?.annualMax ||
          comp?.flexiConfigJson?.annualMax,
        claimAmount: amount,
        billSupportedAmount: amount,
        claimPeriodFrom: fromBounds.from,
        claimPeriodTo: toBounds.to,
        detailsJson: { notes: claimForm.details },
        documentsJson: claimDocuments.length ? claimDocuments : undefined,
        flexiConfig: comp?.flexiConfig || comp?.flexiConfigJson,
      });
      toast.success("Reimbursement claim submitted");
      setClaimForm({
        componentCode: "",
        salaryComponentId: "",
        claimAmount: "",
        claimPeriodFromMonth: "",
        claimPeriodToMonth: "",
        details: "",
      });
      setClaimDocuments([]);
      await load();
      onActionSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Claim failed");
    } finally {
      setBusy(false);
    }
  };

  const viewMode = ["declare", "claim", "hub"].includes(mode) ? mode : "hub";

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-[hsl(var(--ds-muted-fg))]">
        <Loader2 className="animate-spin" size={16} /> Loading Flexi benefits…
      </div>
    );
  }

  if (access && access.enabled === false) {
    return (
      <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-4 py-6 text-sm text-[hsl(var(--ds-muted-fg))]">
        Reimbursement / Flexi is not enabled for your company yet
        {access.effectiveFrom ? ` (effective from ${access.effectiveFrom})` : ""}.
      </div>
    );
  }

  if (access?.structureEligible === false || (components.length === 0 && employeeId)) {
    return (
      <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-4 py-6 text-sm text-[hsl(var(--ds-muted-fg))]">
        No reimbursement components are available on your salary structure yet. Please contact HR if you
        expect to opt in.
      </div>
    );
  }

  const hubOnly = viewMode === "hub";

  return (
    <div className="space-y-6">
      {!hideHeader ? (
        <div>
          <h3 className="text-base font-semibold text-[hsl(var(--ds-fg))]">
            {viewMode === "declare"
              ? "Opt-in / Declare"
              : viewMode === "claim"
                ? "Submit claim"
                : "Reimbursement"}
          </h3>
          <p className="mt-1 text-sm text-[hsl(var(--ds-muted-fg))]">
            {hubOnly
              ? "Balances, opt-in history, claims, and ledger for components on your salary structure."
              : viewMode === "declare"
                ? "Choose a component and declare your amount for this financial year."
                : "Submit a claim with bills against your opt-in."}
          </p>
          {access?.declare && !access.declare.ok && viewMode !== "claim" && (
            <p className="mt-2 text-xs text-[hsl(var(--ds-warning))]">{access.declare.message}</p>
          )}
          {access?.claim && !access.claim.ok && viewMode !== "declare" && (
            <p className="mt-1 text-xs text-[hsl(var(--ds-warning))]">{access.claim.message}</p>
          )}
        </div>
      ) : (
        <>
          {access?.declare && !access.declare.ok && viewMode !== "claim" && (
            <p className="text-xs text-[hsl(var(--ds-warning))]">{access.declare.message}</p>
          )}
          {access?.claim && !access.claim.ok && viewMode !== "declare" && (
            <p className="text-xs text-[hsl(var(--ds-warning))]">{access.claim.message}</p>
          )}
        </>
      )}

      {hubOnly && balances.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[hsl(var(--ds-border))]">
          <table className="min-w-full text-sm">
            <thead className="bg-[hsl(var(--ds-muted))] text-left text-xs text-[hsl(var(--ds-muted-fg))]">
              <tr>
                <th className="px-3 py-2">Component</th>
                <th className="px-3 py-2 text-right">Structure max</th>
                <th className="px-3 py-2 text-right">Declared</th>
                <th className="px-3 py-2 text-right">Approved</th>
                <th className="px-3 py-2 text-right">Exempt</th>
                <th className="px-3 py-2 text-right">Taxable</th>
                <th className="px-3 py-2 text-right">Available</th>
              </tr>
            </thead>
            <tbody>
              {balances.map((b) => (
                <tr key={b.componentCode} className="border-t border-[hsl(var(--ds-border))]">
                  <td className="px-3 py-2 font-medium">{b.componentCode}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{b.structureAnnualMax}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{b.declared}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{b.approved}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[hsl(var(--ds-success))]">{b.exempt}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[hsl(var(--ds-warning))]">{b.taxable}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{b.availableDeclared}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === "declare" && (
      <form onSubmit={submitDeclaration} className="max-w-xl space-y-3 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4">
        {!hideHeader ? (
          <h4 className="text-sm font-semibold">Opt-in for this financial year</h4>
        ) : null}
        <div>
          <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">Component</label>
          <select
            className="w-full rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
            value={declForm.componentCode}
            onChange={(e) =>
              setDeclForm((f) => ({ ...f, componentCode: e.target.value, declarationJson: {}, declaredAmount: "" }))
            }
            required
          >
            <option value="">Select component…</option>
            {components.map((c) => (
              <option key={c.id} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        {declForm.componentCode ? (
          <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-3 py-2 text-xs text-[hsl(var(--ds-fg))]">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span>Maximum eligible / month</span>
              <span className="text-sm font-semibold tabular-nums text-[hsl(var(--ds-fg))]">
                {formatInr(declareLimits.monthly)}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2 text-[hsl(var(--ds-muted-fg))]">
              <span>Maximum for this FY</span>
              <span className="font-medium tabular-nums">{formatInr(declareLimits.annual)}</span>
            </div>
          </div>
        ) : null}
        <div>
          <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
            Opt-in amount for this FY (₹)
          </label>
          <input
            type="number"
            min={0}
            step="1"
            max={declareLimits.annual != null && declareLimits.annual > 0 ? declareLimits.annual : undefined}
            className="w-full rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
            placeholder={
              declareLimits.annual != null
                ? `Enter amount up to ${formatInr(declareLimits.annual)}`
                : "Enter amount"
            }
            value={declForm.declaredAmount}
            onChange={(e) => {
              let v = e.target.value;
              const max = declareLimits.annual;
              if (max != null && max > 0 && v !== "" && Number(v) > max) {
                v = String(max);
              }
              setDeclForm((f) => ({ ...f, declaredAmount: v }));
            }}
            required
          />
        </div>
        {schema.map((field) => (
          <div key={field.key}>
            {field.type === "checkbox" ? (
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={!!declForm.declarationJson[field.key]}
                  onChange={(e) =>
                    setDeclForm((f) => ({
                      ...f,
                      declarationJson: { ...f.declarationJson, [field.key]: e.target.checked },
                    }))
                  }
                />
                {field.label}
                {field.required ? " *" : ""}
              </label>
            ) : field.type === "select" ? (
              <select
                className="w-full rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                value={declForm.declarationJson[field.key] || ""}
                onChange={(e) =>
                  setDeclForm((f) => ({
                    ...f,
                    declarationJson: { ...f.declarationJson, [field.key]: e.target.value },
                  }))
                }
                required={!!field.required}
              >
                <option value="">{field.label}…</option>
                {(field.options || []).map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={field.type === "number" ? "number" : "text"}
                className="w-full rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                placeholder={field.label + (field.required ? " *" : "")}
                value={declForm.declarationJson[field.key] || ""}
                onChange={(e) =>
                  setDeclForm((f) => ({
                    ...f,
                    declarationJson: { ...f.declarationJson, [field.key]: e.target.value },
                  }))
                }
                required={!!field.required}
              />
            )}
          </div>
        ))}
        <button
          type="submit"
          disabled={busy || (access?.declare && !access.declare.ok)}
          className="rounded-lg bg-[hsl(var(--ds-primary))] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? "Saving…" : "Submit opt-in"}
        </button>
      </form>
      )}

      {(hubOnly || viewMode === "declare") && declarations.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">My opt-ins</h4>
          {declarations.slice(0, 20).map((d) => (
            <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2 text-sm">
              <span>
                <span className="font-medium">{d.componentCode}</span> · ₹{d.declaredAmount} · {d.status}
              </span>
              {!["REVOKED", "LOCKED"].includes(d.status) && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => revoke(d.componentCode)}
                  className="text-xs text-red-600 hover:underline"
                >
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {viewMode === "claim" && (
      <form onSubmit={submitClaim} className="max-w-xl space-y-3 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4">
        {!hideHeader ? <h4 className="text-sm font-semibold">New reimbursement claim</h4> : null}
        <div>
          <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">Component</label>
          <select
            className="w-full rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
            value={claimForm.componentCode}
            onChange={(e) => {
              const code = e.target.value;
              const comp = components.find((c) => c.code === code);
              setClaimForm((f) => ({
                ...f,
                componentCode: code,
                salaryComponentId: comp?.id ? String(comp.id) : "",
                claimAmount: "",
              }));
            }}
            required
          >
            <option value="">Select component…</option>
            {components.map((c) => (
              <option key={c.id} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        {claimForm.componentCode ? (
          <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-3 py-2 text-xs text-[hsl(var(--ds-fg))]">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span>Available to claim</span>
              <span className="text-sm font-semibold tabular-nums text-[hsl(var(--ds-fg))]">
                {formatInr(claimBalance?.available)}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[hsl(var(--ds-muted-fg))]">
              <span>Opted-in {formatInr(claimBalance?.declared)}</span>
              <span>Already approved {formatInr(claimBalance?.approved)}</span>
            </div>
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-[11px] text-[hsl(var(--ds-muted-fg))]">Period from (month)</label>
            <input
              type="month"
              className="w-full rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
              value={claimForm.claimPeriodFromMonth}
              onChange={(e) => setClaimForm((f) => ({ ...f, claimPeriodFromMonth: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] text-[hsl(var(--ds-muted-fg))]">Period to (month)</label>
            <input
              type="month"
              className="w-full rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
              value={claimForm.claimPeriodToMonth}
              onChange={(e) => setClaimForm((f) => ({ ...f, claimPeriodToMonth: e.target.value }))}
              required
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">Claim amount (₹)</label>
          <input
            type="number"
            min={0}
            step="1"
            max={
              claimBalance?.available != null && claimBalance.available >= 0
                ? claimBalance.available
                : undefined
            }
            className="w-full rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
            placeholder={
              claimBalance?.available != null
                ? `Enter amount up to ${formatInr(claimBalance.available)}`
                : "Claim amount"
            }
            value={claimForm.claimAmount}
            onChange={(e) => {
              let v = e.target.value;
              const max = claimBalance?.available;
              if (max != null && max >= 0 && v !== "" && Number(v) > max) {
                v = String(max);
              }
              setClaimForm((f) => ({ ...f, claimAmount: v }));
            }}
            required
          />
        </div>
        <textarea
          className="w-full rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
          rows={2}
          placeholder="Details / bill notes"
          value={claimForm.details}
          onChange={(e) => setClaimForm((f) => ({ ...f, details: e.target.value }))}
        />
        <div>
          <label className="mb-1 block text-[11px] text-[hsl(var(--ds-muted-fg))]">Supporting bills</label>
          <input
            type="file"
            multiple
            disabled={uploadBusy || busy}
            className="w-full text-xs"
            onChange={async (e) => {
              const files = [...(e.target.files || [])];
              if (!files.length) return;
              setUploadBusy(true);
              try {
                const uploaded = [];
                for (const file of files) {
                  const ext = file.name.includes(".")
                    ? file.name.slice(file.name.lastIndexOf("."))
                    : "";
                  const storageFileName = `flexi_claim_${Date.now()}_${Math.random()
                    .toString(36)
                    .slice(2, 8)}${ext}`;
                  const info = await uploadFileToR2(file, {
                    employeeId,
                    storageFileName,
                    slotKey: "flexi_reimbursement",
                  });
                  uploaded.push({
                    name: file.name,
                    url: info?.url || info?.publicUrl || info?.fileUrl || info?.key,
                    key: info?.key,
                    uploadedAt: new Date().toISOString(),
                  });
                }
                setClaimDocuments((prev) => [...prev, ...uploaded]);
                toast.success(`${uploaded.length} file(s) uploaded`);
              } catch (err) {
                toast.error(err?.response?.data?.message || "Upload failed");
              } finally {
                setUploadBusy(false);
                e.target.value = "";
              }
            }}
          />
          {claimDocuments.length > 0 ? (
            <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">{claimDocuments.length} file(s) attached</p>
          ) : null}
        </div>
        <button
          type="submit"
          disabled={
            busy ||
            (access?.claim && !access.claim.ok) ||
            (claimBalance != null && !(claimBalance.available > 0))
          }
          className="rounded-lg bg-[hsl(var(--ds-primary))] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? "Submitting…" : "Submit claim"}
        </button>
      </form>
      )}

      {(hubOnly || viewMode === "claim") && claims.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold">My claims</h4>
          <div className="space-y-2">
            {claims.slice(0, 20).map((c) => (
              <div key={c.id} className="rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2 text-sm space-y-1">
                <div>
                  <span className="font-medium">{c.componentCode}</span> · ₹{c.claimAmount} · {c.status}
                  {c.workflow?.pendingLabel ? (
                    <span className="text-[hsl(var(--ds-muted-fg))]"> · Pending: {c.workflow.pendingLabel}</span>
                  ) : null}
                  {c.claimPeriodFrom ? ` · ${c.claimPeriodFrom}→${c.claimPeriodTo || ""}` : ""}
                </div>
                {c.status === "CLARIFICATION" ? (
                  <button
                    type="button"
                    disabled={busy}
                    className="text-xs text-[hsl(var(--ds-primary))] hover:underline"
                    onClick={async () => {
                      const remarks = window.prompt("Your response to the query");
                      if (remarks == null) return;
                      setBusy(true);
                      try {
                        await flexiBenefitsService.respondClaimQuery(c.id, {
                          employeeId,
                          remarks,
                        });
                        toast.success("Response submitted");
                        await load();
                      } catch (err) {
                        toast.error(err?.response?.data?.message || "Failed to respond");
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Respond to query
                  </button>
                ) : null}
                {c.detailsJson?.provisionalPayment?.awaitingBill ? (
                  <button
                    type="button"
                    disabled={busy}
                    className="text-xs text-[hsl(var(--ds-primary))] hover:underline"
                    onClick={async () => {
                      const amt = window.prompt(
                        "Bill-supported eligible amount",
                        String(c.billSupportedAmount || c.approvedAmount || c.claimAmount)
                      );
                      if (amt == null) return;
                      setBusy(true);
                      try {
                        await flexiBenefitsService.supplementBill(c.id, {
                          billSupportedAmount: Number(amt),
                        });
                        toast.success("Bill recorded; tax adjustment posted");
                        await load();
                      } catch (err) {
                        toast.error(err?.response?.data?.message || "Supplement failed");
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Submit supporting bill
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {hubOnly && ledger.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold">Ledger</h4>
          <div className="overflow-x-auto rounded-xl border border-[hsl(var(--ds-border))]">
            <table className="min-w-full text-sm">
              <thead className="bg-[hsl(var(--ds-muted))] text-left text-xs text-[hsl(var(--ds-muted-fg))]">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Component</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Ref</th>
                </tr>
              </thead>
              <tbody>
                {ledger.slice(0, 40).map((row) => (
                  <tr key={row.id} className="border-t border-[hsl(var(--ds-border))]">
                    <td className="px-3 py-2 text-xs text-[hsl(var(--ds-muted-fg))]">
                      {row.createdAt ? String(row.createdAt).slice(0, 10) : "—"}
                    </td>
                    <td className="px-3 py-2 font-medium">{row.componentCode}</td>
                    <td className="px-3 py-2">{row.entryType}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.amount}</td>
                    <td className="px-3 py-2 text-xs text-[hsl(var(--ds-muted-fg))]">{row.reference || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">
            Reimbursement slips appear on payslips when paid through payroll. Download from Payslips tab.
          </p>
        </div>
      )}
    </div>
  );
}
